/**
 * Student web: when closing full-screen AINA, return to the route the user was on
 * before `navigate(/ainavoice|/ainamentor)`. Stored in sessionStorage so deep links
 * to AINA still fall back to dashboard when no prior path was recorded.
 */

const STORAGE_KEY = "aina_return_path_v1";

type SessionStorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

function getSessionStorage(): SessionStorageLike | null {
  if (typeof globalThis === "undefined") return null;
  const g = globalThis as { sessionStorage?: SessionStorageLike };
  return g.sessionStorage ?? null;
}

function isAinaPathname(pathname: string): boolean {
  const first = pathname.replace(/^\//, "").split("/").filter(Boolean)[0];
  return first === "ainavoice" || first === "ainamentor";
}

export function setAinaReturnPathFromLocation(loc: {
  pathname: string;
  search: string;
  hash: string;
}): void {
  const ss = getSessionStorage();
  if (!ss) return;
  if (isAinaPathname(loc.pathname)) return;
  ss.setItem(STORAGE_KEY, `${loc.pathname}${loc.search}${loc.hash}`);
}

/** Call when navigating to AINA from code that does not have React Router `location` (e.g. doubts). */
export function setAinaReturnPathFromWindowLocation(): void {
  if (typeof globalThis === "undefined") return;
  const w = (
    globalThis as {
      window?: { location: { pathname: string; search: string; hash: string } };
    }
  ).window;
  if (!w?.location) return;
  setAinaReturnPathFromLocation({
    pathname: w.location.pathname,
    search: w.location.search,
    hash: w.location.hash,
  });
}

function validateStoredReturnPath(raw: string | null): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith("/")) return null;
  if (trimmed.includes("//")) return null;
  try {
    /** RN/tsconfig libs sometimes define `URL` without WHATWG fields — normalize via cast. */
    const parsed = new URL(trimmed, "https://local.invalid") as unknown as {
      pathname: string;
      search: string;
      hash: string;
    };
    const pathname = parsed.pathname;
    if (isAinaPathname(pathname)) return null;
    return `${pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

/** Returns the stored path once and clears it; `null` if missing or unsafe. */
export function consumeAinaReturnPath(): string | null {
  const ss = getSessionStorage();
  if (!ss) return null;
  const raw = ss.getItem(STORAGE_KEY);
  ss.removeItem(STORAGE_KEY);
  return validateStoredReturnPath(raw);
}

/**
 * Returns the stored path WITHOUT clearing it. Use when downstream surfaces
 * (e.g. AINA → doubt sub-page breadcrumbs) need to derive the source label
 * while leaving the value intact for `consumeAinaReturnPath` to use later.
 */
export function peekAinaReturnPath(): string | null {
  const ss = getSessionStorage();
  if (!ss) return null;
  return validateStoredReturnPath(ss.getItem(STORAGE_KEY));
}
